import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import { updateBookingPageSettings } from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { BookingPageTheme } from "../../features/trial-requests/enums/BookingPageTheme";
import { toUpdateRequest } from "../../features/trial-requests/utils/bookingPage";
import { MARKETING_URL } from "../../routes";

const THEMES: BookingPageTheme[] = ["Auto", "Light", "Dark"];

const snippetFor = (attribute: string, slug: string) =>
  `<div ${attribute}="${slug}"></div>\n<script async src="${MARKETING_URL}widget.js"></script>`;

/**
 * Embed snippets for the instructor's own website (trial form + member
 * sign-in) plus the widget styling: accent color and light/dark theme.
 */
const EmbedWidgetCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const [theme, setTheme] = useState<BookingPageTheme>("Auto");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setTheme(settings.theme);
  }, [settings]);

  const dirty = !!settings && theme !== settings.theme;

  const handleSave = () => {
    if (!settings) return;
    dispatch(
      updateBookingPageSettings({
        ...toUpdateRequest(settings),
        theme,
      }),
    );
  };

  const handleCopy = async (key: string, snippet: string) => {
    await navigator.clipboard.writeText(snippet);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const snippets = settings?.enabled && settings.slug && (
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
      {[
        {
          key: "booking",
          label: t("trialRequests.embedBookingSnippet", "Trial booking form"),
          snippet: snippetFor("data-timeplanned-booking", settings.slug),
        },
        {
          key: "contact",
          label: t("trialRequests.embedContactSnippet", "Contact form"),
          snippet: snippetFor("data-timeplanned-contact", settings.slug),
        },
        {
          key: "portal",
          label: t("trialRequests.embedPortalSnippet", "Member sign-in"),
          snippet: snippetFor("data-timeplanned-portal", settings.slug),
        },
      ].map(({ key, label, snippet }) => (
        <div key={key}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-grey-20 dark:text-white">{label}</p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => handleCopy(key, snippet)}
            >
              <Copy size={16} />
              {copiedKey === key
                ? t("trialRequests.copied", "Copied!")
                : t("trialRequests.embedCopySnippet", "Copy snippet")}
            </button>
          </div>
          <code className="mt-1 block overflow-x-auto whitespace-pre rounded-lg bg-grey-95 px-3 py-2 text-xs text-grey-20 dark:bg-primary-20 dark:text-white">
            {snippet}
          </code>
        </div>
      ))}
    </div>
  );

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("trialRequests.embedTitle", "Embed on your website")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "trialRequests.embedIntro",
          "Paste a snippet into your own website to take trial requests, messages and member sign-ins right there. The widget blends into your page — no frame, no border — and follows the style you pick below.",
        )}
      </p>

      {!settings?.enabled && (
        <p className="mt-4 text-sm text-grey-40 dark:text-grey-80">
          {t(
            "trialRequests.embedEnableFirst",
            "Enable your booking page first — the widgets use its address.",
          )}
        </p>
      )}

      {settings?.enabled && (
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 text-sm text-grey-40 dark:text-grey-80">
            {t("trialRequests.embedTheme", "Theme")}
            <div className="flex gap-2">
              {THEMES.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTheme(value)}
                  className={
                    theme === value
                      ? "rounded-full bg-primary-40 px-4 py-1.5 text-sm font-medium text-white dark:bg-primary-90 dark:text-primary-10"
                      : "rounded-full bg-grey-95 px-4 py-1.5 text-sm font-medium text-grey-20 hover:bg-grey-90 dark:bg-primary-20 dark:text-white dark:hover:bg-[#3E334D]"
                  }
                >
                  {t(`trialRequests.embedTheme${value}`, value)}
                </button>
              ))}
            </div>
          </div>

          {dirty && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={status === "loading"}
            >
              {t("trialRequests.embedSaveStyle", "Save style")}
            </button>
          )}
        </div>
      )}

      {snippets}
    </div>
  );
};

export default EmbedWidgetCard;
